import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { CONFIG } from 'src/config-global';
import { DashboardLayout } from 'src/layouts/dashboard';
import { useAuthContext } from 'src/auth/hooks';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard } from 'src/auth/guard';
import WorkoutDiscussion from 'src/pages/dashboard/workout-discussion/workout-discussion';
import LibraryFoodItemDiscussion from 'src/pages/dashboard/library-food-item-discussion/library-food-item-discussion';
import LibraryDishItemDiscussion from 'src/pages/dashboard/library-dish-item-discussion/library-dish-item-discussion';
import LibraryMealDiscussion from 'src/pages/dashboard/library-meal-discussion/library-meal-discussion';

import NotesHistory from 'src/pages/dashboard/notes-history/notes-history';

import GoalDetail from 'src/pages/dashboard/goal-detail/goal-detail';

import ProgramMembers from 'src/pages/dashboard/program-members/program-members';
import SetDiscussion from 'src/sections/home/set-discussion';
import PlateMembers from 'src/pages/dashboard/plate-members/plate-members';
import LogDiscussion from 'src/sections/home/Log-discussion';
import SetNotesHistory from 'src/sections/home/set-notes-history';
import { Launch } from '@mui/icons-material';
import LogNotesHistory from 'src/sections/home/logs-notes-history';
import FaqDetail from 'src/sections/account/view/FaqsDetails';

// ----------------------------------------------------------------------

const HomePage = lazy(() => import('src/pages/dashboard/home'));
const PersonalHomePage = lazy(() => import('src/pages/dashboard/personal-home'));

const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const AccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const HelpCenter = lazy(() => import('src/pages/dashboard/user/helpCenter'));
const FeedbackPage = lazy(() => import('src/pages/dashboard/user/feedback'));
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const AddTrainerPage = lazy(() => import('src/pages/dashboard/trainer/new'));
const AddMemberPage = lazy(() => import('src/pages/dashboard/member/new'));
const TrainersListPage = lazy(() => import('src/pages/dashboard/trainer/list'));
const MembersListPage = lazy(() => import('src/pages/dashboard/member/list'));
const AdminPersonalMembers = lazy(() => import('src/pages/dashboard/AdminPersonalMembers/list'));
const AdminPlaylistPage = lazy(() => import('src/pages/dashboard/adminPlaylist/list'));
const AdminVideoslistPage = lazy(() => import('src/pages/dashboard/adminVideos/list'));
const AdminPostlistPage = lazy(() => import('src/pages/dashboard/adminPosts/list'));
const AdminGroupslistPage = lazy(() => import('src/pages/dashboard/adminGroups/list'));
const NewsLetterList = lazy(() => import('src/pages/dashboard/news-letter/list'));
const FaqsList = lazy(() => import('src/pages/dashboard/faqs/faqs-list'));
const TicketsList = lazy(() => import('src/pages/dashboard/tickets/tickets-list'));
const FeedbackListPage = lazy(() => import('src/pages/dashboard/feedback/feedback-list'));
const FaqsCategoryList = lazy(() => import('src/pages/dashboard/faqs/faqs-category-list'));
const FaqsSubCategoryList = lazy(() => import('src/pages/dashboard/faqs/faqs-sub-category-list'));
const AdminReferralSystem = lazy(() => import('src/pages/dashboard/admin-referrals'));
const LaunchList = lazy(() => import('src/pages/dashboard/launch-list/list'));
const AdminDashboard = lazy(() => import('src/pages/dashboard/admin_dashboard/list'));
const UserAnalyticsPage = lazy(() => import('src/pages/dashboard/user-analytics/list'));
const UserAnalyticsDetailsPage = lazy(() => import('src/pages/dashboard/user-analytics/details'));
const AnalyticsDetailsPage = lazy(
  () => import('src/sections/home/admin-dashboard/analytics-details-page')
);

const AdminMessagelistPage = lazy(() => import('src/pages/dashboard/adminMessage/list'));
const AddVideoPage = lazy(() => import('src/pages/dashboard/video/new'));
const AddEditClassPage = lazy(() => import('src/pages/dashboard/class/new'));
const ReportPage = lazy(() => import('src/pages/dashboard/report/new'));
const PlaylistPage = lazy(() => import('src/pages/dashboard/playlist'));
const ReportListPage = lazy(() => import('src/pages/dashboard/report_list/list'));
const ModerationListPage = lazy(() => import('src/pages/dashboard/moderation_list/list'));
const PostDetailPage = lazy(() => import('src/pages/dashboard/post'));
const TrainerProfile = lazy(() => import('src/pages/dashboard/trainer/trainerProfile'));
const MemberProfile = lazy(() => import('src/pages/dashboard/personal-member/personal-member'));
const ClassDetail = lazy(() => import('src/pages/dashboard/class/class-detail'));
const ManageActivity = lazy(() => import('src/pages/dashboard/activity/manage-program'));
const ManagePlate = lazy(() => import('src/pages/dashboard/plates/manage-plate'));
const ActivityBundle = lazy(() => import('src/pages/dashboard/activity-bundle'));
const LogHistory = lazy(() => import('src/pages/dashboard/logs-history'));
const FaqHistory = lazy(() => import('src/pages/dashboard/faqs/faq-history'));
const PrivacyPolicy = lazy(() => import('src/pages/privacy_policy'));
const CookiePolicy = lazy(() => import('src/pages/cookie_policy'));
const TermsConditions = lazy(() => import('src/pages/terms_and_conditions'));

const Meal = lazy(() => import('src/pages/dashboard/meal'));
const FoodScanPage = lazy(() => import('src/pages/dashboard/food-scan'));

const IndividualExercise = lazy(() => import('src/pages/dashboard/individual-exercise'));

const Page404 = lazy(() => import('src/pages/error/404'));
const ViewActivity = lazy(() => import('src/pages/dashboard/activity/view-program'));
const ViewPlate = lazy(() => import('src/pages/dashboard/plates/view-plate'));

const layoutContent = (
  <DashboardLayout>
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? <>{layoutContent}</> : <AuthGuard>{layoutContent}</AuthGuard>,
    children: [
      { path: 'home', element: <HomePage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'account', element: <AccountPage /> },
    ],
  },
];

export const dashboardRoutesAdmin = () => {
  // eslint-disable-next-line
  const { user } = useAuthContext();

  let childrens = [];

  // if (user?.role?.id === 3 && user?.gym?.subscription?.type === 'expired' ||user?.gym?.status === 'inactive' &&) {
  if (user?.role?.id === 3 && user?.gym?.status === 'inactive') {
    childrens = [{ path: 'account', element: <AccountPage /> }];
  } else {
    childrens = [
      {
        path: 'home',
        element:
          user?.role?.id === 1 ? (
            <Navigate to="/dashboard/list" replace />
          ) : user?.role?.id === 6 ? (
            <PersonalHomePage />
          ) : (
            <HomePage />
          ),
      },
      {
        path: 'analytics_overview',
        element:
          user?.role?.id === 1 ? <AdminDashboard /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'user-analytics',
        element:
          user?.role?.id === 1 ? <UserAnalyticsPage /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'user-analytics/details/:detailType',
        element:
          user?.role?.id === 1 ? (
            <UserAnalyticsDetailsPage />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'analytics_overview/details/:detailType',
        element:
          user?.role?.id === 1 ? (
            <AnalyticsDetailsPage />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      { path: 'chat', element: <ChatPage /> },
      { path: 'account', element: <AccountPage /> },
      {
        path: 'faq',
        element: user?.role?.id !== 1 ? <HelpCenter /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'faq_history',
        element: user?.role?.id !== 1 ? <FaqHistory /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'feedback',
        element:
          user?.role?.id !== 1 ? <FeedbackPage /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'privacy_policy',
        element: <PrivacyPolicy />,
      },
      {
        path: 'terms_and_conditions',
        element: <TermsConditions />,
      },
      {
        path: 'cookie_policy',
        element: <CookiePolicy />,
      },
      { path: 'faq/faq_detail/:id', element: <FaqDetail /> },
      {
        path: 'list',
        element:
          user?.role?.id === 1 ? <UserListPage /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'trainers',
        element:
          user?.role?.id === 1 ? <TrainersListPage /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'members',
        element:
          user?.role?.id === 1 ? <MembersListPage /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'personal_members',
        element:
          user?.role?.id === 1 ? (
            <AdminPersonalMembers />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'admin_playlists',
        element:
          user?.role?.id === 1 ? <AdminPlaylistPage /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'admin_videos',
        element:
          user?.role?.id === 1 ? (
            <AdminVideoslistPage />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'manage-activity',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <ManageActivity />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'manage-plate',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <ManagePlate />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'view-program',
        element:
          user?.role?.id === 6 || user?.role?.id === 4 ? (
            <ViewActivity />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'view-plate',
        element:
          user?.role?.id === 6 || user?.role?.id === 4 ? (
            <ViewPlate />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'admin_posts',
        element:
          user?.role?.id === 1 ? <AdminPostlistPage /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'admin_groups',
        element:
          user?.role?.id === 1 ? (
            <AdminGroupslistPage />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'news-letter',
        element:
          user?.role?.id === 1 ? <NewsLetterList /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'faqs',
        element: user?.role?.id === 1 ? <FaqsList /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'support-center',
        element: user?.role?.id === 1 ? <TicketsList /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'feedback-list',
        element:
          user?.role?.id === 1 ? <FeedbackListPage /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'faqs-category',
        element:
          user?.role?.id === 1 ? <FaqsCategoryList /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'faqs-sub-category',
        element:
          user?.role?.id === 1 ? (
            <FaqsSubCategoryList />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'referral-system',
        element:
          user?.role?.id === 1 ? (
            <AdminReferralSystem />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'launch-list',
        element: user?.role?.id === 1 ? <LaunchList /> : <Navigate to="/dashboard/home" replace />,
      },
      {
        path: 'admin_messages',
        element:
          user?.role?.id === 1 ? (
            <AdminMessagelistPage />
          ) : (
            <Navigate to="/dashboard/home" replace />
          ),
      },
      {
        path: 'add_trainer',
        element:
          user?.role?.id === 3 ? <AddTrainerPage /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'trainer_profile',
        element:
          user?.role?.id === 5 || user?.role?.id === 3 || user?.role?.id === 6 ? (
            <TrainerProfile />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'member_profile',
        element:
          user?.role?.id === 4 ? <MemberProfile /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'workout-discussion',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <WorkoutDiscussion />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'notes-history',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <NotesHistory />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      // {
      //   path: 'exercise-notes-history',
      //   element:
      //     user?.role?.id === 4 || user?.role?.id === 6 ? (
      //       <NotesHistory />
      //     ) : (
      //       <Navigate to="/dashboard/404" replace />
      //     ),
      // },
      {
        path: 'set-notes-history',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <SetNotesHistory />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'goal-detail',
        element: user?.role?.id === 6 ? <GoalDetail /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'set-discussion',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <SetDiscussion />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'log-notes',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <LogDiscussion />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'note-history',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <LogNotesHistory />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'food-item-discussion',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <LibraryFoodItemDiscussion />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'dish-item-discussion',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <LibraryDishItemDiscussion />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'meal-discussion',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <LibraryMealDiscussion />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'program-members',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <ProgramMembers />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'plate-members',
        element:
          user?.role?.id === 4 || user?.role?.id === 6 ? (
            <PlateMembers />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'activity-bundle',
        element:
          user?.role?.id === 6 ? <ActivityBundle /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'logs-history',
        element: user?.role?.id === 6 ? <LogHistory /> : <Navigate to="/dashboard/404" replace />,
      },

      {
        path: 'meal',
        element: user?.role?.id === 6 ? <Meal /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'food-scan',
        element: user?.role?.id === 6 ? <FoodScanPage /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'individual-exercise',
        element:
          user?.role?.id === 6 ? <IndividualExercise /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'add_member',
        element:
          user?.role?.id === 3 ? <AddMemberPage /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'add_video',
        element: user?.role?.id === 4 ? <AddVideoPage /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'class',
        element:
          user?.role?.id === 4 || user?.role?.id === 3 ? (
            <AddEditClassPage />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'report',
        element: <ReportPage />,
      },

      {
        path: 'class-detail',
        element:
          user?.role?.id === 5 ||
          user?.role?.id === 4 ||
          user?.role?.id === 3 ||
          user?.role?.id === 1 ? (
            <ClassDetail />
          ) : (
            <Navigate to="/dashboard/404" replace />
          ),
      },
      {
        path: 'playlist',
        children: [
          { element: <Navigate to="/dashboard/404" replace />, index: true },
          {
            path: ':id',
            element:
              user?.role?.id === 4 || user?.role?.id === 3 ? (
                <PlaylistPage />
              ) : (
                <Navigate to="/dashboard/404" replace />
              ),
          },
        ],
      },
      {
        path: 'reports/list',
        element:
          user?.role?.id === 1 ? <ReportListPage /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'moderation/list',
        element:
          user?.role?.id === 1 ? <ModerationListPage /> : <Navigate to="/dashboard/404" replace />,
      },
      {
        path: 'post',
        children: [
          { element: <Navigate to="/dashboard/404" replace />, index: true },
          {
            path: ':post_id',
            element:
              user?.role?.id !== 2 ? <PostDetailPage /> : <Navigate to="/dashboard/404" replace />,
          },
        ],
      },
      { path: '404', element: <Page404 /> },
    ];
  }

  return [
    {
      path: 'dashboard',
      element: CONFIG.auth.skip ? <>{layoutContent}</> : <AuthGuard>{layoutContent}</AuthGuard>,
      children: childrens,
    },
  ];
};
